import React from 'react';
import { Button, Modal, Upload, Typography, Divider, Spin, Empty, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { ContractReviewCard } from 'components/contract-review-card/contract-review-card.component';
import { useImportContractsController } from './import-contracts.controller';
import { ImportContractsFragmentProps } from './interfaces';

const { Text } = Typography;

export const ImportContractsFragment: React.FC<ImportContractsFragmentProps> = (props) => {
    const { fileList, results, isExtracting, extractionDone, onFilesChange, onExtract, onConfirm, onCancel } = (
        props.useController ?? useImportContractsController
    )(props);

    const footer = extractionDone
        ? [
              <Button key="cancel" onClick={onCancel}>
                  Cancelar
              </Button>,
              <Button key="confirm" type="primary" onClick={onConfirm}>
                  Entendido, revisar contratos
              </Button>,
          ]
        : [
              <Button key="cancel" onClick={onCancel}>
                  Cancelar
              </Button>,
              <Button
                  key="extract"
                  type="primary"
                  loading={isExtracting}
                  disabled={fileList.length === 0}
                  onClick={onExtract}
              >
                  {isExtracting ? 'Procesando...' : 'Extraer datos'}
              </Button>,
          ];

    return (
        <Modal
            visible={props.visible}
            title="Importar contratos desde archivos"
            onCancel={onCancel}
            footer={footer}
            width={800}
            destroyOnClose
        >
            {!extractionDone && (
                <>
                    <Text type="secondary">
                        Subí uno o varios archivos PDF o DOCX de contratos. El sistema extraerá los datos
                        automáticamente usando IA. Para mejores resultados, asegurate que:
                        <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                            <li>El archivo esté en buen estado y sea legible</li>
                            <li>El texto no esté escaneado como imagen (usar PDF con texto seleccionable)</li>
                            <li>El archivo no exceda los 10MB</li>
                        </ul>
                    </Text>
                    <Divider />
                    <Upload
                        multiple
                        accept=".pdf,.docx"
                        beforeUpload={(file, fileListFromUpload) => {
                            // Check file size (10MB limit)
                            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
                            if (file.size > maxSize) {
                                message.error(`El archivo ${file.name} es demasiado grande. Tamaño máximo: 10MB`);
                                return false;
                            }
                            onFilesChange(fileListFromUpload as unknown as File[]);
                            return false;
                        }}
                        fileList={fileList.map((f) => ({
                            uid: `${f.name}-${f.size}`,
                            name: f.name,
                            size: f.size,
                            type: f.type,
                            status: 'done' as const,
                        }))}
                        onRemove={(file) => {
                            const updated = fileList.filter((f) => `${f.name}-${f.size}` !== file.uid);
                            onFilesChange(updated);
                        }}
                    >
                        <Button icon={<UploadOutlined />}>Seleccionar archivos</Button>
                    </Upload>
                </>
            )}

            {isExtracting && (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <Spin tip="Procesando archivos con IA..." />
                </div>
            )}

            {extractionDone && results.length === 0 && (
                <Empty description="No se pudieron procesar los archivos">
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        Posibles causas:
                        <ul style={{ textAlign: 'left', display: 'inline-block', marginTop: '8px' }}>
                            <li>El archivo está escaneado como imagen</li>
                            <li>El formato no es compatible</li>
                            <li>El archivo está corrupto o protegido</li>
                            <li>Problema de conexión con el servidor</li>
                        </ul>
                        Intentá con otro archivo o contacta soporte.
                    </Text>
                </Empty>
            )}

            {extractionDone && results.length > 0 && (
                <>
                    <Text>
                        Se procesaron <strong>{results.length}</strong> archivo(s). Revisá los datos extraídos antes de
                        continuar a la creación de contratos.
                    </Text>
                    <Divider />
                    {results.map((r, i) => (
                        <ContractReviewCard key={i} data={r} />
                    ))}
                </>
            )}
        </Modal>
    );
};
